<script lang="ts">
  import { pop } from "svelte-spa-router";
  import ListNavigationItem from "$shared/components/ListNavigationItem.svelte";
  import Separator from "$shared/components/Separator.svelte";
  import CardInline from "$shared/components/CardInline.svelte";
  import { onMount } from "svelte";
  import CustomButton from "$shared/components/CustomButton.svelte";
  import {
    BodyText,
    Page,
    LicensePlateManagerCard,
    LoadingIndicatorSmall,
    ModalDialog,
  } from "$components";
  import { Center, Ver } from "$components";
  import {
    type AgreementSpot,
    type CreateSpotShare,
    type RentalInfo,
  } from "../../../apis/parkito2";
  import { withApi } from "$shared/lib/api-parkito";
  import { Toast } from "$components";
  import { formatDate } from "$shared/utils/dateTime";
  import SpotSharingAdd from "$shared/components/parkito/spotsharing/SpotSharingAdd.svelte";
  import {
    AgreementAccessModel,
    AgreementModel,
  } from "$shared/models/mod.ts";
  import { myPages } from "../stores/myPagesData.svelte.ts";
  import api, { fromApi } from "$shared/lib/api-parkito2.svelte.ts";

  type Props = { params: { id: string; spotid: string } };
  const { params }: Props = $props();
  const myPagesData = $derived($myPages.call.data);
  let showEndContractDialog: boolean = $state(false);
  const agreementAccess: AgreementAccessModel | undefined = $derived(
    myPagesData?.findAgreementBySpot(params.spotid),
  );

  const agreement: AgreementModel | undefined = $derived(
    agreementAccess?.agreement,
  );
  const spot: AgreementSpot | undefined = $derived(
    myPagesData?.findAgreementSpot(params.spotid),
  );
  const siteRentalInfo: RentalInfo | null | undefined = $derived(
    myPagesData?.findSite(params.id)?.rentalInfo,
  );
  let showSpotSharingDialog: boolean = $state(false);

  const spotId = $derived(spot?.spotId);
  const spotSharings = fromApi((spotId: string) =>
    api.searchSpotShares({
      body: {
        pagination: { pageSize: 9999, page: 1 },
        spotIds: [spotId],
      },
    })
  );

  onMount(async () => {
    if (import.meta.env.DEV) {
      console.log("Profile mounted. Id: " + params?.id);
    }
    $spotSharings.load(spotId!);
  });

  const toggleEndContractDialog = () => {
    showEndContractDialog = !showEndContractDialog;
  };

  const addSpotSharing = async (data: CreateSpotShare) => {
    const res = await withApi((api) => {
      return api.createSpotShare(data);
    });

    if (res.isOk()) {
      Toast.info("Platsdelning har skapats");
      $spotSharings.reload(spotId!);
      toggleAddSpotSharing();
    } else {
      Toast.error("Kunde inte skapa platsdelning");
    }
  };

  const toggleAddSpotSharing = () => {
    showSpotSharingDialog = !showSpotSharingDialog;
  };
</script>

<Page backButton={true} title="Plats {spot?.name ?? "-"}">
  {#snippet content()}
    <CardInline title="Platsinformation">
      <Ver gap="none">
        <ListNavigationItem
          title="Namn"
          textRight={spot?.name}
          isFirst={true}
        />
        <ListNavigationItem
          title="Plats"
          textRight={spot?.numericName.toString()}
        />
        <ListNavigationItem
          title="Adress"
          textRight={siteRentalInfo?.address ?? "-"}
        />
        <ListNavigationItem
          title="Typ"
          textRight={agreement?.translateProductKind()}
          isLast={true}
        />
        <!--<ListNavigationItem
          title="Dörröppning"
          textRight="Bluetooth, fysisk tagg"
          isLast={true}
        />-->
      </Ver>
    </CardInline>

    {#if agreement?.isActive() && !agreement?.isPermit()}
      <CardInline
        title="Platsdelning"
        buttonTitle="Skapa ny"
        buttonClick={toggleAddSpotSharing}
      >
        <img
          src="/graphics/keys.svg"
          alt="parkando-cars"
          class="mx-auto pt-[8px] pb-[32px]"
        />
        <p class="px-[16px] pb-[24px]">
          Låna ut din plats till någon du känner via en platsdelning.
        </p>
        {#if $spotSharings.isLoading()}
          <Center>
            <LoadingIndicatorSmall visible />
          </Center>
        {:else if $spotSharings.call.data}
          {@const sharedSpots = $spotSharings.call.data.items}
          <div>
            {#each sharedSpots as spotSharing}
              <ListNavigationItem
                title={spotSharing?.sentToEmail ?? "-"}
                description={`Skapad ${
                  formatDate(spotSharing?.createdAt, "YYYY-MM-DD HH:mm")
                }`}
                textRight={spotSharing?.acceptedAt ? "Aktiv" : "Inbjuden"}
                isFirst={spotSharing === sharedSpots[0]}
                link={`#/site/${spotSharing.siteId}/spot/${spotSharing.spot.id}/share/${spotSharing.id}`}
                isLast={spotSharing === sharedSpots[sharedSpots.length - 1]}
              />
            {/each}
          </div>
        {/if}
      </CardInline>
    {/if}

    {#if agreement?.permit}
      <LicensePlateManagerCard
        permit={agreement.getPermit()!}
        accessLevel={agreementAccess!.accessLevel}
        reloadData={() => $myPages.reload()}
      />
    {/if}

    <!--
    <CardInline title="Avtal">
      <ListNavigationItem
        title="Avtalsnummer"
        textRight="19790109-XXXX"
        isFirst={true}
      />
      <ListNavigationItem
        title="Avtalsperiod"
        textRight="2024-01-01 → 2024-12-31"
      />
      <ListNavigationItem title="Status" textRight="Aktiv" />
      <ListNavigationItem
        title="Pris per månad (inkl moms)"
        textRight="900,00 SEK"
      />
      <ListNavigationItem
        title="Uppsägningstid"
        textRight="1 kalendermånad"
        isLast={true}
      />

      <ListNavigationItem
        title="Användarvillkor"
        description="Dokument"
        labelRight="PDF"
        link="#/site/00000030-0000-0000-0000-0020302302320/sharing?downloadPdf"
        isFirst={true}
        isLast={true}
        customClass="mt-[24px]"
      />

      <CustomButton
        type="warning"
        onClick={toggleEndContractDialog}
        customClass="mt-[32px] w-full !max-w-full !h-[50px]"
      >
        Avsluta avtal
      </CustomButton>
    </CardInline>
    -->
  {/snippet}
</Page>

  <!-- End contract modal dialog -->
  {#if showEndContractDialog}
    <ModalDialog onBackdropPressed={toggleEndContractDialog}>
      <div class="px-[24px] py-[24px] max-mobile:px-[16px]">
        <h2 class="text-[#181818] text-[18px]">Bekräfta avsluta avtal</h2>
      </div>
      <Separator />
      <div class="px-[24px] py-[16px] max-mobile:px-[16px]">
        <p class="py-[16px]">Är du säker på att du vill avsluta ditt avtal?</p>
        <div class="flex pt-[24px] justify-end">
          <CustomButton
            type="ghost"
            onClick={toggleEndContractDialog}
            customClass="mr-[8px]"
          >Avbryt</CustomButton>
          <CustomButton
            type="warning"
            onClick={() => {
              if (import.meta.env.DEV) {
                console.log(
                  "end-contract-clicked",
                );
              }
            }}
            customClass="w-[200px]"
          >Avsluta avtal</CustomButton>
        </div>
      </div>
    </ModalDialog>
  {/if}

  <!-- Show add spot sharing modal dialog -->
  {#if showSpotSharingDialog}
    <ModalDialog onBackdropPressed={toggleAddSpotSharing}>
      <div class="px-[24px] py-[24px] max-mobile:px-[16px]">
        <h2 class="text-[#181818] text-[18px]">Skapa ny platsdelning</h2>
      </div>
      <Separator />
      <SpotSharingAdd
        agreementSpotId={spot?.id}
        spotId={spot?.spotId ?? ""}
        abortClicked={toggleAddSpotSharing}
        submitClicked={addSpotSharing}
      />
    </ModalDialog>
  {/if}
